import type { DeviceHealthStatus } from '@pos/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';

import { Btn } from '../../components/Btn';
import { Field } from '../../components/Field';
import { Input } from '../../components/Input';
import { KpiCard } from '../../components/KpiCard';
import { PageHeader } from '../../components/PageHeader';
import { Pill } from '../../components/Pill';
import { Select } from '../../components/Select';
import { DataTable } from '../../components/Table';
import { listBranches } from '../../data/fetchers';
import { dashboardApi } from '../../lib/apiClient';
import { fmtInt } from '../../lib/format';
import { visibleBranchIds } from '../../lib/permissions';
import { useSession } from '../../stores/session';
import { useUi } from '../../stores/ui';

const STATUS_TONE: Record<
  DeviceHealthStatus['status'],
  'ok' | 'warn' | 'danger'
> = {
  online: 'ok',
  stale: 'warn',
  offline: 'danger',
};

const CREDENTIAL_STATE = {
  active: { label: 'พร้อมใช้งาน', tone: 'ok' },
  refresh_required: { label: 'ต้องกู้คืน', tone: 'warn' },
  revoked: { label: 'เพิกถอนแล้ว', tone: 'danger' },
} as const;

function fmtDateTime(value: string | null): string {
  if (!value) {
    return 'ยังไม่เคย sync';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString('th-TH', {
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

export function DeviceHealthView() {
  const queryClient = useQueryClient();
  const branchId = useUi((state) => state.branchId);
  const sessionUser = useSession((state) => state.user);
  const { data, isLoading } = useQuery({
    queryKey: ['device-health', branchId],
    queryFn: () =>
      dashboardApi.getDeviceHealth({
        branchId: branchId ?? undefined,
      }),
  });
  const { data: branches = [], isLoading: branchesLoading } = useQuery({
    queryKey: ['branches'],
    queryFn: listBranches,
  });

  const [label, setLabel] = useState('');
  const [selectedBranchId, setSelectedBranchId] = useState(branchId ?? '');
  const [enrollCode, setEnrollCode] = useState('');
  const [enrollError, setEnrollError] = useState('');
  const [enrollment, setEnrollment] = useState<{
    code: string;
    expiresAt: string;
  } | null>(null);
  const [selectedAction, setSelectedAction] = useState<{
    kind: 'revoke' | 'recovery';
    deviceId: string;
    label: string;
  } | null>(null);
  const [actionFeedback, setActionFeedback] = useState<{
    kind: 'revoke' | 'recovery';
    deviceId: string;
    error?: string;
    result?: { code: string; expiresAt: string };
  } | null>(null);

  const canManageDevices =
    sessionUser?.role === 'owner' || sessionUser?.role === 'manager';
  const canCreateDevice = canManageDevices;
  const canManageCredentials = canManageDevices;
  const visibleBranches = useMemo(() => {
    if (!sessionUser) return [];
    const allowedIds = visibleBranchIds(
      sessionUser,
      branches.map((branch) => branch.id),
    );
    return branches.filter((branch) => allowedIds.includes(branch.id));
  }, [branches, sessionUser]);
  const manageableBranchIds = useMemo(
    () =>
      sessionUser
        ? new Set(
            visibleBranchIds(
              sessionUser,
              data?.devices.map((device) => device.branchId) ?? [],
            ),
          )
        : new Set<string>(),
    [sessionUser, data?.devices],
  );

  const revokeMutation = useMutation({
    mutationFn: (deviceId: string) => dashboardApi.revokeDevice(deviceId),
    onMutate: (deviceId) => {
      setActionFeedback({ kind: 'revoke', deviceId });
    },
    onSuccess: async (_result, deviceId) => {
      setActionFeedback(null);
      setSelectedAction((current) =>
        current?.kind === 'revoke' && current.deviceId === deviceId
          ? null
          : current,
      );
      await queryClient.invalidateQueries({ queryKey: ['device-health'] });
    },
    onError: (error, deviceId) => {
      setActionFeedback({
        kind: 'revoke',
        deviceId,
        error: error instanceof Error ? error.message : 'เพิกถอนไม่สำเร็จ',
      });
    },
  });
  const recoveryMutation = useMutation({
    mutationFn: (input: { deviceId: string; label: string }) =>
      dashboardApi.createDeviceRecoveryCode(input.deviceId, {
        label: input.label,
      }),
    onMutate: ({ deviceId }) => {
      setActionFeedback({ kind: 'recovery', deviceId });
    },
    onSuccess: async (result, { deviceId }) => {
      setActionFeedback({
        kind: 'recovery',
        deviceId,
        result: { code: result.code, expiresAt: result.expiresAt },
      });
      await queryClient.invalidateQueries({ queryKey: ['device-health'] });
    },
    onError: (error, { deviceId }) => {
      setActionFeedback({
        kind: 'recovery',
        deviceId,
        error:
          error instanceof Error ? error.message : 'สร้างรหัสกู้คืนไม่สำเร็จ',
      });
    },
  });
  const deviceMutationPending =
    revokeMutation.isPending || recoveryMutation.isPending;

  const createCodeMutation = useMutation({
    mutationFn: () =>
      dashboardApi.createDeviceEnrollment({
        branchId: selectedBranchId,
        label: label.trim(),
      }),
    onSuccess: (result) => {
      setEnrollCode(result.code);
      setEnrollment({ code: result.code, expiresAt: result.expiresAt });
    },
    onError: (error) => {
      setEnrollError(
        error instanceof Error
          ? error.message || 'สร้างรหัสไม่สำเร็จ'
          : 'สร้างรหัสไม่สำเร็จ',
      );
    },
  });

  useEffect(() => {
    setSelectedBranchId((current) => {
      if (
        branchId &&
        visibleBranches.some((branch) => branch.id === branchId)
      ) {
        return branchId;
      }
      if (visibleBranches.some((branch) => branch.id === current)) {
        return current;
      }
      return visibleBranches[0]?.id ?? '';
    });
  }, [branchId, visibleBranches]);

  const columns = useMemo<ColumnDef<DeviceHealthStatus, unknown>[]>(
    () => [
      { accessorKey: 'deviceId', header: 'Device' },
      { accessorKey: 'branchId', header: 'สาขา' },
      {
        accessorKey: 'status',
        header: 'สถานะ',
        cell: (cell) => (
          <Pill tone={STATUS_TONE[cell.row.original.status]}>
            {cell.row.original.status}
          </Pill>
        ),
      },
      {
        accessorKey: 'credentialState',
        header: 'ข้อมูลรับรอง',
        cell: (cell) => {
          const state = CREDENTIAL_STATE[cell.row.original.credentialState];
          return <Pill tone={state.tone}>{state.label}</Pill>;
        },
      },
      {
        accessorKey: 'pendingEventCount',
        header: 'Pending',
        cell: (cell) => (
          <span className="num">{fmtInt(cell.getValue<number>())}</span>
        ),
      },
      {
        accessorKey: 'failedEventCount',
        header: 'Failed',
        cell: (cell) => (
          <span className="num">{fmtInt(cell.getValue<number>())}</span>
        ),
      },
      {
        accessorKey: 'dlqCount',
        header: 'DLQ',
        cell: (cell) => (
          <span className="num">{fmtInt(cell.getValue<number>())}</span>
        ),
      },
      {
        accessorKey: 'currentMasterDataVersion',
        header: 'Master Data',
        cell: (cell) => {
          const row = cell.row.original;
          return (
            <span className="num">
              {fmtInt(row.currentMasterDataVersion)} /{' '}
              {fmtInt(row.latestMasterDataVersion)}
            </span>
          );
        },
      },
      {
        accessorKey: 'lastSeenAt',
        header: 'ล่าสุด',
        cell: (cell) => fmtDateTime(cell.getValue<string | null>()),
      },
      {
        accessorKey: 'appVersion',
        header: 'App',
        cell: (cell) => cell.getValue<string | null>() ?? '—',
      },
      ...(canManageCredentials
        ? [
            {
              id: 'actions',
              header: 'จัดการ',
              cell: (cell: { row: { original: DeviceHealthStatus } }) => {
                const device = cell.row.original;
                const canManageRow =
                  sessionUser?.role === 'owner' ||
                  manageableBranchIds.has(device.branchId);
                if (!canManageRow) {
                  return null;
                }
                return (
                  <div className="flex gap-2">
                    <Btn
                      kind="ghost"
                      type="button"
                      disabled={deviceMutationPending}
                      onClick={() => {
                        setActionFeedback(null);
                        setSelectedAction({
                          kind: 'revoke',
                          deviceId: device.deviceId,
                          label: device.label,
                        });
                      }}
                    >
                      เพิกถอนเครื่อง {device.deviceId}
                    </Btn>
                    <Btn
                      kind="ghost"
                      type="button"
                      disabled={deviceMutationPending}
                      onClick={() => {
                        setActionFeedback(null);
                        setSelectedAction({
                          kind: 'recovery',
                          deviceId: device.deviceId,
                          label: device.label,
                        });
                      }}
                    >
                      กู้คืนเครื่อง {device.deviceId}
                    </Btn>
                  </div>
                );
              },
            } satisfies ColumnDef<DeviceHealthStatus, unknown>,
          ]
        : []),
    ],
    [
      canManageCredentials,
      deviceMutationPending,
      manageableBranchIds,
      sessionUser?.role,
    ],
  );

  return (
    <div className="bo-fade">
      <PageHeader
        title="เครื่อง POS"
        subtitle="online/stale/offline · pending/failed/DLQ · master-data drift"
      />
      <div className="grid gap-4 md:grid-cols-4 mb-6">
        <KpiCard
          label="Devices"
          value={isLoading ? '…' : fmtInt(data?.totalDevices ?? 0)}
        />
        <KpiCard
          label="Online"
          value={isLoading ? '…' : fmtInt(data?.onlineCount ?? 0)}
          tone="ok"
        />
        <KpiCard
          label="Stale"
          value={isLoading ? '…' : fmtInt(data?.staleCount ?? 0)}
          tone="warn"
        />
        <KpiCard
          label="Offline"
          value={isLoading ? '…' : fmtInt(data?.offlineCount ?? 0)}
          tone="danger"
        />
      </div>
      {canCreateDevice ? (
        <section className="bo-card mb-6 p-5 sm:p-6">
          <h2 className="text-h3 font-bold text-ink mb-4">เพิ่มเครื่อง POS</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="สาขา">
              <Select
                id="activation-branch"
                value={selectedBranchId}
                onChange={(event) => setSelectedBranchId(event.target.value)}
                disabled={branchesLoading || visibleBranches.length === 0}
              >
                {visibleBranches.length === 0 ? (
                  <option value="">
                    {branchesLoading ? 'กำลังโหลดสาขา' : 'ไม่พบสาขาที่เลือกได้'}
                  </option>
                ) : null}
                {visibleBranches.map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.short}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="ชื่อเครื่อง">
              <Input
                id="activation-name"
                placeholder="เช่น เคาน์เตอร์หน้าโรงอาหาร"
                value={label}
                onChange={(event) => setLabel(event.target.value)}
              />
            </Field>
          </div>
          <div className="mt-4 flex items-center gap-3">
            <Btn
              kind="primary"
              type="button"
              onClick={() => {
                setEnrollError('');
                setEnrollCode('');
                setEnrollment(null);
                createCodeMutation.mutate();
              }}
              disabled={createCodeMutation.isPending || !selectedBranchId}
            >
              {createCodeMutation.isPending ? 'กำลังสร้าง' : 'สร้างรหัส'}
            </Btn>
            {enrollment && (
              <span className="font-mono text-xl tracking-[0.1em] text-ink">
                รหัส: {enrollCode}
              </span>
            )}
          </div>
          {enrollError && (
            <p className="mt-3 text-sm-label text-danger">{enrollError}</p>
          )}
        </section>
      ) : null}
      <DataTable columns={columns} data={data?.devices ?? []} />
      {selectedAction ? (
        <section className="bo-card mt-4" aria-live="polite">
          {selectedAction.kind === 'revoke' ? (
            <>
              <h2 className="font-bold">
                ยืนยันเพิกถอนเครื่อง {selectedAction.deviceId}
              </h2>
              <p className="mt-2">
                ซิงก์คลาวด์จะหยุด แต่ยอดขายออฟไลน์ยังอยู่ในเครื่อง
              </p>
              <Btn
                kind="primary"
                type="button"
                disabled={revokeMutation.isPending}
                onClick={() => revokeMutation.mutate(selectedAction.deviceId)}
              >
                {revokeMutation.isPending ? 'กำลังเพิกถอน' : 'ยืนยันเพิกถอน'}
              </Btn>
            </>
          ) : (
            <>
              <h2 className="font-bold">
                ยืนยันกู้คืนเครื่อง {selectedAction.deviceId}
              </h2>
              <Btn
                kind="primary"
                type="button"
                disabled={recoveryMutation.isPending}
                onClick={() =>
                  recoveryMutation.mutate({
                    deviceId: selectedAction.deviceId,
                    label: selectedAction.label,
                  })
                }
              >
                {recoveryMutation.isPending
                  ? 'กำลังสร้างรหัสกู้คืน'
                  : 'ยืนยันสร้างรหัสกู้คืน'}
              </Btn>
            </>
          )}
          {actionFeedback?.kind === selectedAction.kind &&
          actionFeedback.deviceId === selectedAction.deviceId &&
          actionFeedback.error ? (
            <p role="alert" className="mt-3 text-danger">
              {actionFeedback.error}
            </p>
          ) : null}
          {actionFeedback?.kind === 'recovery' &&
          actionFeedback.deviceId === selectedAction.deviceId &&
          actionFeedback.result ? (
            <p className="mt-3">
              รหัสกู้คืน:{' '}
              <span className="font-mono">{actionFeedback.result.code}</span> ·
              หมดอายุ {fmtDateTime(actionFeedback.result.expiresAt)}
            </p>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}
